import { CoreApi, Dimension, Product, ProductType } from '@inter-ikea-storageone/range';
import { HierarchyUtils, ComponentIds, Entity, Nullable } from '@inter-ikea-core/core';

const filterByReplaceType = (
  product: Product,
  previousProduct: Product,
  replaceType: Dimension
) => {
  if (replaceType === 'height') {
    return product.communicatedDimensions.height !== previousProduct.communicatedDimensions.height;
  } else {
    return product.communicatedDimensions.height === previousProduct.communicatedDimensions.height;
  }
};

export const getReplaceableDoors = (doors: Product[], handleId?: string) => {
  if (handleId) {
    return [
      {
        doors,
        handleId
      }
    ];
  } else {
    return [
      {
        doors
      }
    ];
  }
};

// Returns a list with matching doors and their handles if there are any
export const getMatchingDoorsWithHandles = (
  previousDoors: Product[] | undefined,
  products: Product[],
  coreApi: CoreApi,
  replaceType: Dimension
) => {
  if (!previousDoors) return [];

  return previousDoors?.flatMap(previousDoor => {
    const previousDoorEntity = coreApi.entities.find(previousDoor.entityId);

    if (!previousDoorEntity) return [];

    // Get the handle previously used on the door
    const handle = HierarchyUtils.hierarchy(previousDoorEntity).find(entity =>
      entity.hasComponent(ComponentIds.HANDLE_COMPONENT_ID)
    );

    const matchingDoors = products
      .filter(product => {
        return (
          product.type === previousDoor?.type &&
          product.communicatedDimensions.width === previousDoor.communicatedDimensions.width &&
          product.design === previousDoor.design &&
          product.name === previousDoor.name &&
          product.typeName === previousDoor.typeName
        );
      })
      .filter(p => filterByReplaceType(p, previousDoor, replaceType)) as Product[];

    if (!matchingDoors || matchingDoors.length === 0) return [];

    if (handle) {
      const handleId = coreApi.entities.getProductRef(handle);
      if (!handleId) return { doors: matchingDoors };

      return {
        doors: matchingDoors,
        handleId: handleId
      };
    } else {
      return {
        doors: matchingDoors
      };
    }
  });
};

export const getMatchingSlidingDoors = (
  previousProduct: Product,
  products: Product[],
  replaceType: Dimension
) => {
  const newSlidingDoors = products
    .filter(
      product =>
        product.type === previousProduct?.type &&
        product.communicatedDimensions.width === previousProduct.communicatedDimensions.width &&
        product.design === previousProduct.design &&
        product.name === previousProduct.name &&
        product.typeName === previousProduct.typeName
    )
    .filter(p => filterByReplaceType(p, previousProduct, replaceType))
    .flatMap(p => getReplaceableDoors([p]));

  return newSlidingDoors;
};

export const getDoorsFromSelectedEntity = (
  coreApi: CoreApi,
  selectedEntity: Nullable<Entity>,
  products: Product[]
) => {
  if (!selectedEntity) return;

  const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(selectedEntity, [
    ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
    ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
  ]);

  const getDoorProducts = (doors: Entity[]) => {
    return doors
      .map(door => {
        const doorProductEntity = coreApi.entities.findProductEntity(door.id);
        const foundDoor = products.find(product => {
          return product.coreProductId === doorProductEntity?.productId;
        });

        return {
          ...foundDoor,
          entityId: doorProductEntity?.entityId
        };
      })
      .filter(Boolean) as Product[];
  };

  // If selected entity contains a sliding door return the sliding door product
  if (slidingDoor) {
    const doorProducts = getDoorProducts([slidingDoor]);
    return doorProducts;
  }

  // Otherwise get all the connected doors from the entity and return those products instead
  const doors = HierarchyUtils.hierarchy(selectedEntity).filter(entity =>
    entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID)
  );

  const doorProducts = getDoorProducts(doors);

  return doorProducts;
};
